import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Snackbar,
  Typography,
  makeStyles,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
  Fab,
  IconButton,
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoIcon from '@material-ui/icons/Info';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import { critiqueCv } from 'lib/Api/CvCritique';
import { CvCritiqueResponse } from 'lib/Model/CvCritique';
import ReactMarkdown from 'react-markdown';
import { getUserData } from 'lib/Helper/Auth';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '80vh',
  },
  formControl: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  cardContent: {
    paddingTop: 0,
  },
  input: {
    display: 'none',
  },
  fileName: {
    marginLeft: theme.spacing(1),
    fontStyle: 'italic',
  },
  uploadButton: {
    marginRight: theme.spacing(1),
  },
  feedbackContainer: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  feedbackTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  scoreDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  scoreValue: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  scoreBar: {
    height: 8,
    width: 60,
    borderRadius: 4,
    marginTop: 2,
  },
  highlightSection: {
    borderLeft: `4px solid ${theme.palette.success.main}`,
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  improvementSection: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  resultsContainer: {
    maxHeight: '700px',
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    position: 'relative',
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
  },
  headerWithLink: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  markdownContent: {
    '& p': { marginBottom: theme.spacing(2) },
  },
  dropZone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backgroundColor: theme.palette.background.default,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  dragActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  uploadIcon: {
    fontSize: 48,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  fileInfo: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressContainer: {
    marginTop: theme.spacing(2),
  },
  scoreIcon: {
    marginRight: theme.spacing(1),
  },
  summaryScore: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  avgScore: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
  },
  summaryScores: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  },
  miniScore: {
    flex: '0 0 45%',
    margin: theme.spacing(0.5, 0),
    display: 'flex',
    alignItems: 'center',
  },
  accordionRoot: {
    marginBottom: theme.spacing(2),
    '&:before': {
      display: 'none',
    },
  },
  accordionSummary: {
    padding: theme.spacing(0, 2),
  },
  accordionDetails: {
    flexDirection: 'column',
    padding: theme.spacing(0, 2, 2, 2),
  },
  sticky: {
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  centeredCard: {
    maxWidth: 600,
    margin: '0 auto',
  },
  twoColumnLayout: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  formColumn: {
    flex: '0 0 100%',
    [theme.breakpoints.up('md')]: {
      flex: '0 0 40%',
      maxWidth: '40%',
    },
  },
  resultsColumn: {
    flex: '0 0 100%',
    [theme.breakpoints.up('md')]: {
      flex: '0 0 60%',
      maxWidth: '60%',
    },
  },
  analysingProgress: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  copyFab: {
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
  },
}));

const FeedbackSection: React.FC<{
  title: string;
  score: number;
  highlight: string;
  improvement: string;
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
}> = ({ title, score, highlight, improvement, expanded = false, onChange }) => {
  const classes = useStyles();

  const getScoreColor = (score: number) => {
    if (score >= 4) return '#4caf50';
    if (score >= 3) return '#ff9800';
    return '#f44336';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 4)
      return (
        <CheckCircleIcon
          style={{ color: '#4caf50' }}
          className={classes.scoreIcon}
        />
      );
    if (score >= 3)
      return (
        <WarningIcon
          style={{ color: '#ff9800' }}
          className={classes.scoreIcon}
        />
      );
    return (
      <ErrorIcon style={{ color: '#f44336' }} className={classes.scoreIcon} />
    );
  };

  const scoreColor = getScoreColor(score);

  return (
    <Accordion
      className={classes.accordionRoot}
      expanded={expanded}
      onChange={(_, isExpanded) => onChange && onChange(isExpanded)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className={classes.accordionSummary}
        aria-label={`${title} section - score ${score}/5`}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {getScoreIcon(score)} {title}
          </Typography>
          <div className={classes.scoreDisplay}>
            <Typography
              variant="body2"
              className={classes.scoreValue}
              style={{ color: scoreColor }}
            >
              {score}/5
            </Typography>
            <LinearProgress
              className={classes.scoreBar}
              variant="determinate"
              value={score * 20}
              style={{ backgroundColor: '#e0e0e0' }}
              color={
                score >= 4 ? 'primary' : score >= 3 ? 'secondary' : 'secondary'
              }
            />
          </div>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <div className={classes.highlightSection}>
          <Typography variant="subtitle1" gutterBottom>
            Highlights
          </Typography>
          <div className={classes.markdownContent}>
            <ReactMarkdown>{highlight}</ReactMarkdown>
          </div>
        </div>

        <div className={classes.improvementSection}>
          <Typography variant="subtitle1" gutterBottom>
            Areas for Improvement
          </Typography>
          <div className={classes.markdownContent}>
            <ReactMarkdown>{improvement}</ReactMarkdown>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const CVCritiqueView: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [analysisStage, setAnalysisStage] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [critique, setCritique] = useState<CvCritiqueResponse | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const userData = getUserData();
  const hasPermission = userData?.permissions?.includes('cv_critique');

  if (!hasPermission) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <Typography variant="h6" color="error">
          You do not have permission to access this feature.
        </Typography>
      </Box>
    );
  }

  const simulateUploadProgress = () => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 300);
    return () => clearInterval(interval);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    } else {
      setSelectedFile(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);
    setAnalysisStage('Uploading CV...');

    const cleanupUploadProgress = simulateUploadProgress();

    try {
      setTimeout(() => {
        setUploadProgress(100);
        setAnalysisStage('Analysing CV...');
      }, 1500);

      const response = await critiqueCv({
        resume: selectedFile,
      });

      setCritique(response);
      setSnackbarMessage('CV critique generated successfully!');
      setSnackbarOpen(true);

      setExpandedSection('QuantifiedAchievements');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'An error occurred while generating the CV critique',
      );
    } finally {
      setLoading(false);
      cleanupUploadProgress();
      setAnalysisStage('');
      setUploadProgress(0);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleClearFields = () => {
    setCritique(null);
    setSelectedFile(null);
    setExpandedSection(null);
    const resumeInput = document.getElementById(
      'cv-upload',
    ) as HTMLInputElement;
    if (resumeInput) resumeInput.value = '';
  };

  const handleOpenInfoDialog = () => {
    setIsInfoDialogOpen(true);
  };

  const handleCloseInfoDialog = () => {
    setIsInfoDialogOpen(false);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (
        file.type === 'application/pdf' ||
        file.type === 'application/msword' ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        setSelectedFile(file);
      } else {
        setSnackbarMessage('Please upload a PDF or Word document');
        setSnackbarOpen(true);
      }
    }
  };

  const copyToClipboard = () => {
    if (!critique) return;

    const content = `
CV ASSESSMENT RESULTS

SCORES:
- Quantified Achievements: ${critique.QuantifiedAchievementsScore}/5
- Career Progression & Scope: ${critique.CareerProgressionScopeScore}/5
- Clarity & Structure: ${critique.ClarityStructureScore}/5
- Language Accuracy & Precision: ${critique.LanguageAccuracyPrecisionScore}/5
- Visual Professionalism & ATS Friendliness: ${critique.VisualProfessionalismATSFriendlinessScore}/5

DETAILED FEEDBACK:

Quantified Achievements
Highlights: ${critique.QuantifiedAchievementsHighlight}
Areas for Improvement: ${critique.QuantifiedAchievementsImprovement}

Career Progression & Scope
Highlights: ${critique.CareerProgressionScopeHighlight}
Areas for Improvement: ${critique.CareerProgressionScopeImprovement}

Clarity & Structure
Highlights: ${critique.ClarityStructureHighlight}
Areas for Improvement: ${critique.ClarityStructureImprovement}

Language Accuracy & Precision
Highlights: ${critique.LanguageAccuracyPrecisionHighlight}
Areas for Improvement: ${critique.LanguageAccuracyPrecisionImprovement}

Visual Professionalism & ATS Friendliness
Highlights: ${critique.VisualProfessionalismATSFriendlinessHighlight}
Areas for Improvement: ${critique.VisualProfessionalismATSFriendlinessImprovement}
    `;

    navigator.clipboard.writeText(content);
    setSnackbarMessage('Copied to clipboard!');
    setSnackbarOpen(true);
  };

  const calculateAverageScore = () => {
    if (!critique) return 0;

    const scores = [
      critique.QuantifiedAchievementsScore,
      critique.CareerProgressionScopeScore,
      critique.ClarityStructureScore,
      critique.LanguageAccuracyPrecisionScore,
      critique.VisualProfessionalismATSFriendlinessScore,
    ];

    return scores.reduce((sum, score) => sum + score, 0) / scores.length;
  };

  const handleSectionChange = (section: string) => (expanded: boolean) => {
    setExpandedSection(expanded ? section : null);
  };

  const avgScore = calculateAverageScore();
  const getScoreColor = (score: number) => {
    if (score >= 4) return theme.palette.success.main;
    if (score >= 3) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={3}
        className={!critique ? undefined : classes.twoColumnLayout}
      >
        <Grid item xs={12} className={classes.formColumn}>
          <Card className={!critique ? classes.centeredCard : undefined}>
            <CardHeader
              title={
                <div className={classes.headerWithLink}>
                  <span>CV Critique Tool</span>
                  <Tooltip title="Learn how this works">
                    <IconButton
                      size="small"
                      onClick={handleOpenInfoDialog}
                      aria-label="How does this work?"
                    >
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              }
              subheader="Upload a candidate's CV to receive a detailed critique and improvement suggestions"
            />
            <CardContent className={classes.cardContent}>
              <Box className={classes.formControl} mt={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Upload CV (Required)
                </Typography>

                <input
                  accept=".pdf,.doc,.docx"
                  className={classes.input}
                  id="cv-upload"
                  type="file"
                  onChange={handleFileChange}
                />

                <label
                  htmlFor="cv-upload"
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <div
                    className={`${classes.dropZone} ${
                      isDragActive ? classes.dragActive : ''
                    }`}
                  >
                    <CloudUploadIcon className={classes.uploadIcon} />
                    <Typography variant="body1" gutterBottom>
                      Drag and drop the CV here or click to browse
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Supported formats: PDF, DOCX (Max size: 5MB)
                    </Typography>

                    {selectedFile && (
                      <div className={classes.fileInfo}>
                        <FileCopyIcon
                          fontSize="small"
                          style={{ marginRight: 8 }}
                        />
                        <Typography variant="body2">
                          {selectedFile.name} (
                          {Math.round(selectedFile.size / 1024)} KB)
                        </Typography>
                      </div>
                    )}
                  </div>
                </label>

                {loading && (
                  <div className={classes.progressContainer}>
                    <Typography variant="body2" gutterBottom align="center">
                      {analysisStage}
                    </Typography>
                    <LinearProgress
                      variant={
                        analysisStage === 'Uploading CV...'
                          ? 'determinate'
                          : 'indeterminate'
                      }
                      value={uploadProgress}
                      className={classes.analysingProgress}
                    />
                  </div>
                )}
              </Box>
            </CardContent>
            <CardActions>
              <Button
                type="button"
                color="primary"
                variant="contained"
                onClick={handleSubmit}
                disabled={!selectedFile || loading}
                startIcon={
                  loading && <CircularProgress size={20} color="inherit" />
                }
                fullWidth={isMobile}
              >
                {loading ? 'Analysing...' : 'Analyse CV'}
              </Button>
              <Button
                type="button"
                color="default"
                variant="outlined"
                onClick={handleClearFields}
                disabled={loading}
                style={{ marginLeft: '8px' }}
                fullWidth={isMobile}
              >
                Clear
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {loading && !critique && (
          <Grid item xs={12} className={classes.resultsColumn}>
            <Skeleton variant="rect" height={500} />
          </Grid>
        )}

        {!loading && error && (
          <Grid item xs={12} className={classes.resultsColumn}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}

        {!loading && critique && (
          <Grid item xs={12} className={classes.resultsColumn}>
            <Card>
              <CardHeader title="CV Assessment Results" />
              <CardContent>
                <Paper elevation={1} className={classes.summaryScore}>
                  <Typography variant="h6" gutterBottom>
                    Overall Score
                  </Typography>
                  <Typography
                    className={classes.avgScore}
                    style={{ color: getScoreColor(avgScore) }}
                  >
                    {avgScore.toFixed(1)}/5
                  </Typography>

                  <div className={classes.summaryScores}>
                    <div className={classes.miniScore}>
                      <Typography variant="body2">
                        Quantified Achievements:
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          marginLeft: 'auto',
                          color: getScoreColor(
                            critique.QuantifiedAchievementsScore,
                          ),
                        }}
                      >
                        {critique.QuantifiedAchievementsScore}/5
                      </Typography>
                    </div>
                    <div className={classes.miniScore}>
                      <Typography variant="body2">
                        Career Progression:
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          marginLeft: 'auto',
                          color: getScoreColor(
                            critique.CareerProgressionScopeScore,
                          ),
                        }}
                      >
                        {critique.CareerProgressionScopeScore}/5
                      </Typography>
                    </div>
                    <div className={classes.miniScore}>
                      <Typography variant="body2">
                        Clarity & Structure:
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          marginLeft: 'auto',
                          color: getScoreColor(critique.ClarityStructureScore),
                        }}
                      >
                        {critique.ClarityStructureScore}/5
                      </Typography>
                    </div>
                    <div className={classes.miniScore}>
                      <Typography variant="body2">
                        Language & Precision:
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          marginLeft: 'auto',
                          color: getScoreColor(
                            critique.LanguageAccuracyPrecisionScore,
                          ),
                        }}
                      >
                        {critique.LanguageAccuracyPrecisionScore}/5
                      </Typography>
                    </div>
                    <div className={classes.miniScore}>
                      <Typography variant="body2">
                        Visual & ATS Friendliness:
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          marginLeft: 'auto',
                          color: getScoreColor(
                            critique.VisualProfessionalismATSFriendlinessScore,
                          ),
                        }}
                      >
                        {critique.VisualProfessionalismATSFriendlinessScore}/5
                      </Typography>
                    </div>
                  </div>
                </Paper>

                <div className={classes.resultsContainer}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    Detailed Assessment
                  </Typography>

                  <FeedbackSection
                    title="Quantified Achievements"
                    score={critique.QuantifiedAchievementsScore}
                    highlight={critique.QuantifiedAchievementsHighlight}
                    improvement={critique.QuantifiedAchievementsImprovement}
                    expanded={expandedSection === 'QuantifiedAchievements'}
                    onChange={handleSectionChange('QuantifiedAchievements')}
                  />

                  <FeedbackSection
                    title="Career Progression & Scope"
                    score={critique.CareerProgressionScopeScore}
                    highlight={critique.CareerProgressionScopeHighlight}
                    improvement={critique.CareerProgressionScopeImprovement}
                    expanded={expandedSection === 'CareerProgressionScope'}
                    onChange={handleSectionChange('CareerProgressionScope')}
                  />

                  <FeedbackSection
                    title="Clarity & Structure"
                    score={critique.ClarityStructureScore}
                    highlight={critique.ClarityStructureHighlight}
                    improvement={critique.ClarityStructureImprovement}
                    expanded={expandedSection === 'ClarityStructure'}
                    onChange={handleSectionChange('ClarityStructure')}
                  />

                  <FeedbackSection
                    title="Language Accuracy & Precision"
                    score={critique.LanguageAccuracyPrecisionScore}
                    highlight={critique.LanguageAccuracyPrecisionHighlight}
                    improvement={critique.LanguageAccuracyPrecisionImprovement}
                    expanded={expandedSection === 'LanguageAccuracyPrecision'}
                    onChange={handleSectionChange('LanguageAccuracyPrecision')}
                  />

                  <FeedbackSection
                    title="Visual Professionalism & ATS Friendliness"
                    score={critique.VisualProfessionalismATSFriendlinessScore}
                    highlight={
                      critique.VisualProfessionalismATSFriendlinessHighlight
                    }
                    improvement={
                      critique.VisualProfessionalismATSFriendlinessImprovement
                    }
                    expanded={
                      expandedSection === 'VisualProfessionalismATSFriendliness'
                    }
                    onChange={handleSectionChange(
                      'VisualProfessionalismATSFriendliness',
                    )}
                  />

                  <Typography
                    variant="caption"
                    display="block"
                    style={{ marginTop: 16, fontStyle: 'italic' }}
                  >
                    Disclaimer: This automated assessment is meant as a guide.
                    For the most accurate feedback, please consult with a CV
                    writing professional.
                  </Typography>
                </div>

                <Fab
                  color="primary"
                  size="medium"
                  className={classes.copyFab}
                  onClick={copyToClipboard}
                  aria-label="Copy to clipboard"
                >
                  <FileCopyIcon />
                </Fab>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success">
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Dialog
          open={isInfoDialogOpen}
          onClose={handleCloseInfoDialog}
          aria-labelledby="info-dialog-title"
        >
          <DialogTitle id="info-dialog-title">
            How the CV Critique Tool Works
          </DialogTitle>
          <DialogContent>
            <DialogContentText component="div">
              <Typography variant="body1" paragraph>
                The CV Critique Tool helps you provide candidates with
                professional feedback on their CVs. Here's how it works:
              </Typography>

              <Typography variant="subtitle1" gutterBottom>
                The tool evaluates CVs across five key dimensions:
              </Typography>

              <ol>
                <li>
                  <strong>Quantified Achievements</strong> - Does the CV
                  effectively showcase achievements with specific metrics and
                  results?
                </li>
                <li>
                  <strong>Career Progression & Scope</strong> - Is there a clear
                  progression in the candidate's career path? Are roles and
                  responsibilities well defined?
                </li>
                <li>
                  <strong>Clarity & Structure</strong> - Is the CV
                  well-organized, easy to read, and logically structured?
                </li>
                <li>
                  <strong>Language Accuracy & Precision</strong> - Is the
                  language professional, error-free, and effective at conveying
                  the candidate's experience?
                </li>
                <li>
                  <strong>Visual Professionalism & ATS Friendliness</strong> -
                  Is the format professional and would it pass through Applicant
                  Tracking Systems?
                </li>
              </ol>

              <Typography variant="body1" paragraph>
                For each dimension, the tool provides:
              </Typography>
              <ul>
                <li>A score out of 5</li>
                <li>Highlights of what the candidate did well</li>
                <li>Specific suggestions for improvement</li>
              </ul>

              <Alert severity="info" style={{ marginTop: '16px' }}>
                This AI-driven assessment is meant to complement your
                professional expertise, not replace it. Feel free to use the
                generated critique as a starting point for your own feedback to
                candidates.
              </Alert>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseInfoDialog} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </div>
  );
};

export default CVCritiqueView;
